import React, { useState } from 'react';

import { useGraphQLMutation, useGraphQLQuery } from '@betterment/graphql';
import { Banner, Box, Button, Checkbox, FormikField as Field, H4, Input, Select, Text } from '@betterment/samba-react';
import { useNotifications } from '@betterment/shared-components';
import { isEqual } from 'es-toolkit/compat';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { UpdateUserPostalAddressesDocument } from './graphql/mutation.updateUserPostalAddress.generated';
import { EditProfileDocument } from './graphql/query.editProfile.generated';
import HighlightsLoader from './HighlightsLoader';

import { convertDisplayableErrors } from '../Shared/displayable-errors';
import { locationAssign } from '../Shared/path-helpers';

export type EditAddressFormProps = {
  hasPendingIraWithdrawals: boolean;
  isMaybeCheckingUser: boolean;
  onCloseUrl: string;
};

type PostalAddress = {
  line1: string;
  line2: string;
  city: string;
  stateCode: string;
  postalCode: string;
};

type PostalValues = {
  mailingAddress: PostalAddress;
  residentialAddress: PostalAddress;
};

export const EditAddressForm: React.FC<React.PropsWithChildren<EditAddressFormProps>> = ({
  hasPendingIraWithdrawals = false,
  isMaybeCheckingUser = false,
  onCloseUrl,
}) => {
  const [isMailingAddressSameLocal, setIsMailingAddressSameLocal] = useState<boolean>();
  const [isFormAvailable, setIsFormAvailable] = useState<boolean>(true);

  const { data } = useGraphQLQuery(EditProfileDocument, { variables: {} });
  const [updateAddress, { error: mutationError }] = useGraphQLMutation(UpdateUserPostalAddressesDocument);

  const { setNotification } = useNotifications();

  // Respect locally-provided address state once provided. Fall back to server-provided address state. Default to false.
  const isProvidingOneAddress = isMailingAddressSameLocal ?? data?.user.isMailingSameAsResidentialAddress ?? false;

  const initialValues: PostalValues = {
    mailingAddress: {
      city: data?.user.mailingAddress?.city ?? '',
      line1: data?.user.mailingAddress?.line1 ?? '',
      line2: data?.user.mailingAddress?.line2 ?? '',
      postalCode: data?.user.mailingAddress?.postalCode ?? '',
      stateCode: data?.user.mailingAddress?.state?.code ?? '',
    },
    residentialAddress: {
      city: data?.user.residentialAddress?.city ?? '',
      line1: data?.user.residentialAddress?.line1 ?? '',
      line2: data?.user.residentialAddress?.line2 ?? '',
      postalCode: data?.user.residentialAddress?.postalCode ?? '',
      stateCode: data?.user.residentialAddress?.state?.code ?? '',
    },
  };

  const addressStates = data?.addressStateOptions.map(state => ({ text: state.name, value: state.code })) ?? [];

  const validationSchema = Yup.object().shape({
    mailingAddress: Yup.object({
      line1: Yup.string().max(255, 'Address 1 is too long'),
      line2: Yup.string().nullable().max(255, 'Address 2 is too long'),
      city: Yup.string().max(255, 'City is too long'),
      stateCode: Yup.string().oneOf(addressStates.flatMap(state => state.value)),
      postalCode: Yup.string().min(5, 'Invalid zip code').max(9, 'Invalid zip code'),
    }),
    residentialAddress: Yup.object({
      line1: Yup.string().required('Address 1 is required').max(255, 'Address 1 is too long'),
      line2: Yup.string().nullable().max(255, 'Address 2 is too long'),
      city: Yup.string().required('City is required').max(255, 'City is too long'),
      stateCode: Yup.string()
        .required('State is required')
        .oneOf(addressStates.flatMap(state => state.value)),
      postalCode: Yup.string().required('Zip is required').min(5, 'Invalid zip code').max(9, 'Invalid zip code'),
    }),
  });

  const processForm = async (values: PostalValues, { setErrors, setSubmitting }: FormikHelpers<PostalValues>) => {
    setSubmitting(true);

    const residentialAddress = {
      city: values.residentialAddress?.city,
      line1: values.residentialAddress?.line1,
      line2: values.residentialAddress?.line2,
      postalCode: values.residentialAddress?.postalCode,
      stateCode: values.residentialAddress?.stateCode,
    };

    const mailingAddress = {
      city: values.mailingAddress?.city ?? '',
      line1: values.mailingAddress?.line1 ?? '',
      line2: values.mailingAddress?.line2 ?? '',
      postalCode: values.mailingAddress?.postalCode ?? '',
      stateCode: values.mailingAddress?.stateCode ?? '',
    };

    const updateAddressResponse = await updateAddress({
      variables: {
        input: {
          mailingAddress: isProvidingOneAddress ? residentialAddress : mailingAddress,
          residentialAddress,
        },
      },
    });

    const isUpdatedResidentialAddress = !isEqual(initialValues.residentialAddress, residentialAddress);
    const isDifferentInitialAddressValues = !isEqual(initialValues.mailingAddress, initialValues.residentialAddress);
    const isUpdatedMailingAddress = !isEqual(initialValues.mailingAddress, mailingAddress);
    const isResidentialAndMailingAddressUpdated =
      (isUpdatedMailingAddress && isUpdatedResidentialAddress) ||
      (isUpdatedResidentialAddress && isProvidingOneAddress);
    let notificationMessage: string;

    if (isResidentialAndMailingAddressUpdated) {
      notificationMessage = 'Your residential and mailing addresses have been updated.';
    } else if (isUpdatedResidentialAddress) {
      notificationMessage = 'Your residential address has been updated.';
    } else if (isUpdatedMailingAddress || (isProvidingOneAddress && isDifferentInitialAddressValues)) {
      notificationMessage = 'Your mailing address has been updated.';
    } else {
      notificationMessage = 'Your personal information was saved successfully.';
    }

    if (
      updateAddressResponse.data?.updateUserPostalAddress.displayableErrors.length &&
      updateAddressResponse.data.updateUserPostalAddress.displayableErrors.length > 0
    ) {
      const errors = convertDisplayableErrors(updateAddressResponse.data.updateUserPostalAddress.displayableErrors);
      setErrors(errors);
      setSubmitting(false);
    } else {
      setNotification({ type: 'success', message: notificationMessage });

      setIsFormAvailable(false);
      locationAssign(onCloseUrl);
    }
  };

  if (!data) {
    return <HighlightsLoader />;
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={processForm}
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      {({ isSubmitting }) => {
        const isFormSubmissionAvailable = isFormAvailable && !isSubmitting;
        return (
          <Form data-testid="editAddressForm">
            {mutationError && <Text color="text.critical">{mutationError.message}</Text>}
            {isMaybeCheckingUser && (
              <H4 variant="title300" css={{ paddingTop: 'space.100', paddingBottom: 'space.200' }}>
                Residential Address
              </H4>
            )}
            <Box css={{ display: 'grid', gap: 'space.200', maxWidth: '75%' }}>
              <Field name="residentialAddress.line1">
                <Field.Label>Address line 1</Field.Label>
                <Field.Control>
                  <Input />
                </Field.Control>
                <Field.Description>This address can’t be a P.O. box</Field.Description>
                <Field.ErrorMessage />
              </Field>

              <Field name="residentialAddress.line2">
                <Field.Label>Address line 2</Field.Label>
                <Field.Control>
                  <Input />
                </Field.Control>
                <Field.ErrorMessage />
              </Field>

              <Field name="residentialAddress.city">
                <Field.Label>City</Field.Label>
                <Field.Control>
                  <Input />
                </Field.Control>
                <Field.ErrorMessage />
              </Field>

              <Field name="residentialAddress.stateCode">
                <Field.Label>State</Field.Label>
                <Field.Control>
                  <Select>
                    <Select.Trigger aria-label="Select a state">
                      <Select.Value placeholder="Select a state..." />
                    </Select.Trigger>
                    <Select.Content>
                      {addressStates.map(state => (
                        <Select.Item key={state.value} value={state.value}>
                          {state.text}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select>
                </Field.Control>
                <Field.ErrorMessage />
              </Field>

              <Field name="residentialAddress.postalCode">
                <Field.Label>Zip</Field.Label>
                <Field.Control>
                  <Input />
                </Field.Control>
                <Field.ErrorMessage />
              </Field>

              {isMaybeCheckingUser && (
                <>
                  <Checkbox
                    checked={isProvidingOneAddress}
                    onCheckedChange={value => setIsMailingAddressSameLocal(value === true)}
                  >
                    <Checkbox.Control aria-label="This is also my mailing address" />
                    <Checkbox.Label>This is also my mailing address</Checkbox.Label>
                  </Checkbox>

                  {!isProvidingOneAddress && (
                    <>
                      <H4 variant="title300">Mailing Address</H4>

                      <Field name="mailingAddress.line1">
                        <Field.Label>Address line 1</Field.Label>
                        <Field.Control>
                          <Input />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>

                      <Field name="mailingAddress.line2">
                        <Field.Label>Address line 2</Field.Label>
                        <Field.Control>
                          <Input />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>

                      <Field name="mailingAddress.city">
                        <Field.Label>City</Field.Label>
                        <Field.Control>
                          <Input />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>

                      <Field name="mailingAddress.stateCode">
                        <Field.Label>State</Field.Label>
                        <Field.Control>
                          <Select>
                            <Select.Trigger aria-label="Select a state">
                              <Select.Value placeholder="Select a state..." />
                            </Select.Trigger>
                            <Select.Content>
                              {addressStates.map(state => (
                                <Select.Item key={state.value} value={state.value}>
                                  {state.text}
                                </Select.Item>
                              ))}
                            </Select.Content>
                          </Select>
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>

                      <Field name="mailingAddress.postalCode">
                        <Field.Label>Zip</Field.Label>
                        <Field.Control>
                          <Input />
                        </Field.Control>
                        <Field.ErrorMessage />
                      </Field>
                    </>
                  )}
                </>
              )}

              {hasPendingIraWithdrawals && (
                <Banner variant="warning">
                  <Banner.Content>
                    <Text>Changes to residential address will not affect withholding on pending IRA withdrawals.</Text>
                  </Banner.Content>
                </Banner>
              )}

              <Button disabled={!isFormSubmissionAvailable} type="submit" css={{ width: 'fit-content' }}>
                {isFormSubmissionAvailable ? 'Update' : 'Updating Address...'}
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};
